import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@/ui';
import { CheckCircleIcon } from '@/ui';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/state/store';
import { OnboardingService } from '@/shared/api/services';

type InviteSummary = {
  lenderName?: string;
};

export default function ObThankYou(): JSX.Element {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const [invite, setInvite] = useState<InviteSummary | null>(null);
  const [storedLenderName, setStoredLenderName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!user?.userId) return;
        const invites = await OnboardingService.getOpenBankInvitationsByUserId(user.userId, {
          skipErrorCapture: true,
        });
        const first = Array.isArray(invites) ? invites[0] : undefined;
        if (cancelled) return;
        const next: InviteSummary = {};
        if (typeof first?.lenderName === 'string' && first.lenderName.trim().length > 0) {
          next.lenderName = first.lenderName.trim();
        }
        setInvite(next);
      } catch {
        if (cancelled) return;
        setInvite(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.userId]);

  useEffect(() => {
    try {
      if (!user?.userId) return;
      const raw = localStorage.getItem(`ob_invite_${user.userId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { lenderName?: unknown };
      const lenderName = typeof parsed?.lenderName === 'string' ? parsed.lenderName.trim() : '';
      if (lenderName.length > 0) {
        setStoredLenderName(lenderName);
      }
    } catch {
      // ignore
    }
  }, [user?.userId]);

  const partnerName = useMemo(() => {
    const raw = invite?.lenderName;
    if (typeof raw === 'string' && raw.trim().length > 0) return raw.trim();
    if (typeof storedLenderName === 'string' && storedLenderName.trim().length > 0) return storedLenderName.trim();
    return 'your creditor';
  }, [invite?.lenderName, storedLenderName]);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout({ source: 'manual' });
    } finally {
      navigate('/auth', { replace: true });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            backgroundColor: '#E6FAEF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 48, color: '#16a34a' }} />
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: '#0b3b3b',
            mb: 1.5,
          }}
        >
          Thank You!
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            color: '#7b8794',
            maxWidth: 520,
            lineHeight: 1.5,
          }}
        >
          Your information is successfully shared with{' '}
          <Box component="span" sx={{ fontWeight: 700, color: '#6b7280' }}>
            {partnerName}
          </Box>
        </Typography>
      </Box>

      <Box
        sx={{
          width: '100%',
          px: 3,
          pb: 5,
        }}
      >
        <Box
          sx={{
            borderTop: '1px solid #e5e7eb',
            pt: 3,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              void handleLogout();
            }}
            sx={{
              backgroundColor: '#d4e03b',
              color: '#003d3b',
              textTransform: 'none',
              borderRadius: '12px',
              px: 8,
              py: 1.5,
              fontSize: '18px',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#c5d630',
                boxShadow: 'none',
              },
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

 